import Sunny16BaseService from '@/services/Sunny16BaseService'

import type { LocationAvailabilityResult } from '@VelocitySquared/cady-sunny16-commerce-integration-models'
import type {
  LocationModel,
  ReserveAppointmentRequest,
  ReserveAppointmentResult,
  ScheduleAvailabilityRequest
} from '@VelocitySquared/cady-sunny16-commerce-integration-models'
import type {
  BookingSchoolDto,
  BookingSchoolSimpleDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import type { TierLevelSurchargeResultDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront/booking'

class Sunny16CommerceCadyService extends Sunny16BaseService {
  constructor() {
    super(import.meta.env.VITE_SUNNY_16_API_BASE_URL, 'Sunny16CommerceCadyService')
  }

  /**
   * Returns the full list of bookable schools for use in the FindSchoolModal search.
   */
  async getSchoolList(): Promise<BookingSchoolSimpleDto[]> {
    const response = await this.axiosInstance.get<BookingSchoolSimpleDto[]>('cady/school/bookable')
    // The endpoint can return entries with null/empty schoolName (and other fields). Drop them so
    // downstream sort/filter logic can rely on schoolName being a usable string.
    return response.data.filter((s) => s.schoolName && s.schoolName.trim().length > 0)
  }
  /**
   * Returns full school details for booking, including studio locations,
   * available scenes, and yearbook/portfolio flags.
   */
  async getSchoolDetails(schoolId: number): Promise<BookingSchoolDto> {
    try {
      const response = await this.axiosInstance.get(`cady/school/booking/${schoolId}`)
      return response.data
    } catch (error) {
      throw error
    }
  }
  /**
   * Returns the locations (studios) associated with a school.
   */
  async getLocationsBySchoolId(schoolId: number): Promise<LocationModel[]> {
    try {
      const response = await this.axiosInstance.get(`cady/locationsbyschoolid/${schoolId}`)
      return response.data
    } catch (error) {
      throw error
    }
  }
  /**
   * Returns schedule availability (dates + time slots) for a location,
   * school, and session type combination.
   */
  async getLocationAvailability(
    request: ScheduleAvailabilityRequest
  ): Promise<LocationAvailabilityResult> {
    try {
      const response = await this.axiosInstance.post('cady/locationavailability', request)
      return response.data
    } catch (error) {
      throw error
    }
  }
  /**
   * Places a temporary hold on an appointment slot for the given school,
   * location, session type, and start time.
   */
  async reserveAppointment(request: ReserveAppointmentRequest): Promise<ReserveAppointmentResult> {
    try {
      const response = await this.axiosInstance.post('cady/reserveappointment', request)
      return response.data
    } catch (error) {
      throw error
    }
  }
  /**
   * Returns the surcharge prices for each tier level for a given catalog.
   * Used to display per-day pricing in the session calendar.
   */
  async getTierLevelSurcharges(catalogId: string): Promise<TierLevelSurchargeResultDto[]> {
    try {
      const response = await this.axiosInstance.get<TierLevelSurchargeResultDto[]>(
        'storefront/booking/catalog/tier-level-surcharges',
        { params: { catalogId } }
      )
      return response.data
    } catch (error) {
      throw error
    }
  }
}

export default Sunny16CommerceCadyService
